// R51 — HER ROOM'S OWN WRITE, AND THE FOUR SENTENCES THAT REACHED NOBODY.
//
// THE DEFECT THIS FILE EXISTS TO CLOSE, measured 2026-08-01. `POST /api/room/state` is written from
// THREE call sites in App.tsx — `chooseTheme`, `chooseLanguage` and `persistRoomState` — and every
// one of them ended in **`.catch(() => {})`**. Not the payload, not even the status code. That is a
// step WORSE than R47's two surfaces, which at least read `res.ok`.
//
// `handleRoomStateSet` (server/index.js) can answer FOUR ways and composed a sentence for each:
//
//   * **401** — "Sign in to save your room."
//   * **HTTP 200 with `{ ok: false }`** when `ensureUser` comes back empty. R39's shape, R47's
//     shape, a THIRD route: a refusal wearing a success status.
//   * **400** — "That is not one of the themes." / "That is not one of the languages this house
//     speaks." ITEM 6 / 14(c)'s REFUSE-DON'T-REPLACE law, which exists precisely so a value the two
//     sides disagree about is not silently swapped for a default.
//   * **500** — "Your room did not save. Please try again.", whose own comment reads *"A write that
//     touched no row must not report success."*
//
// NOT ONE OF THOSE FOUR SENTENCES REACHED ANYBODY. The client has already painted the new theme,
// language, background, room name or open panels, so she sees it work and finds out on the next
// reload, if ever — and the route's own comment is broken from the client side, which is R23's shape
// exactly: a file states the law and the caller two files away breaks it.
//
// **THIS ROUTE HOLDS HER THEME, HER LANGUAGE, HER ROOM NAME, HER OPEN PANELS AND HER BACKGROUND**,
// and LR15 already found one live blanking bug inside it (`setRoomState` replaced `room_state`
// wholesale, so a rename in My Room deleted LR14's open panels).
//
// So the transport lives here ONCE and hands back an ANSWER rather than a status code (R31: the
// crossing stops existing rather than being watched). `src/lib/saved-readings.ts` is the same file
// one route over and the answer shape is deliberately identical — a third shape for the same
// four-way question is a third answer key.
//
// This module imports NOTHING (the entry requirement for `scripts/`-loadable law).

export const roomStatePath = "/api/room/state";

// A PARTIAL patch, always. `room_state` is a jsonb MERGE at the database (LR14's law), so a surface
// sends the keys it owns and nothing else — the theme control has no business knowing what her
// background is. The type is deliberately open: five surfaces own five different key sets, and a
// closed union here would be a second roster of what My Room may store.
export type RoomStatePatch = Record<string, unknown>;

// THE SHAPE MY ROOM KEEPS. The write side is an open patch on purpose; the READ side is not, because
// five keys is exactly what this room draws itself from and a missing one has to become a default
// somewhere. It became one FOUR TIMES, hand-typed, once per reader.
export type MyRoomState = {
  background: string;
  panels: Record<string, boolean>;
  welcomed: boolean;
  reportsSeen: boolean;
  roomName: string;
};

// >>> BREACH-1 — ONE READ, TWO ANSWERS, AND NEVER TWO FETCHES (R28/R29/R31). <<<
//
// The room read now also answers `needsBirthDetails` — the server refusing a room to an account with
// no birth date, which is her clause *the client cannot skip what the server refuses*. A second
// function with its own `fetch` would be a second round trip on every member change AND two answers
// to one question, so this is the ONE read and `getRoomState` is DERIVED from it.
//
// **PRESENCE, NEVER TRUTHINESS (R53/R136/R137)** on the flag: it arrives off JSON, where
// `Boolean("false")` is `true`, and what it decides is whether a member is sent to fill in her birth
// details. A read that FAILED answers `false` rather than routing her — a bad minute of network is
// not the house telling her she has no room (R18), and the identity guard plus the two-way hydrate
// already hold the screen in that case.
export async function readRoomAccess(
  doFetch: typeof fetch = fetch,
): Promise<{ state: RoomStatePatch | null; needsBirthDetails: boolean }> {
  try {
    const res = await doFetch(roomStatePath, { cache: "no-store" });
    if (!res.ok) { await res.body?.cancel?.().catch(() => {}); return { state: null, needsBirthDetails: false }; }
    const data = await res.json().catch(() => null) as
      | { state?: unknown; needsBirthDetails?: unknown } | null;
    const state = data?.state;
    return {
      state: state && typeof state === "object" ? (state as RoomStatePatch) : null,
      needsBirthDetails: data?.needsBirthDetails === true,
    };
  } catch {
    return { state: null, needsBirthDetails: false };
  }
}

// THE READ, and it is the same route's other half. A read that fails is NOT an answer here (R18):
// there is nothing to say and nothing to change, so the defaults stand and this returns null rather
// than a shape full of empty strings that would overwrite them. It is DERIVED from `readRoomAccess`
// so the state and the refusal can never come from two different reads of the same route.
export async function getRoomState(doFetch: typeof fetch = fetch): Promise<RoomStatePatch | null> {
  return (await readRoomAccess(doFetch)).state;
}

// R60 — THE PICKER THAT OPENS WITH NOTHING IN IT. `/api/room/backgrounds` is the room's OTHER read
// and it lives here rather than in a module of its own, because the thing it lists is the value the
// route above stores. Its one call site was `r.ok ? r.json() : { backgrounds: [] }` guarded by
// `Array.isArray(d.backgrounds)` — R55's shape exactly, where the fabricated fallback IS an array
// and sails through the guard that looks like protection — and the customize modal then draws
//
//     <div className="room-bg-grid">{backgrounds.map(...)}</div>
//
// with nothing in it. **AN EMPTY PICKER IS INDISTINGUISHABLE FROM A HOUSE THAT SHIPS NO
// BACKGROUNDS**, which is R58's finding one control down, and the server was telling the same lie:
// `handleRoomBackgrounds` caught its own error and answered 200 `{ backgrounds: [] }`.
//
// This route CANNOT REFUSE — it reads no session and takes no input, so there is no 401 and no
// `{ ok: false }` to tell apart, exactly like R58's five config menus. So the answer is `T[] | null`
// and deliberately not the four-way one: a refusal branch for a route that has none is a second
// answer key for a question nobody asks.
export const roomBackgroundsPath = "/api/room/backgrounds";

// HER ORIENTATION LAW (2026-08-07) — **THE VIEWPORT RIDES IN THE REQUEST, because the filtering is
// the SERVER's.** A page that asked for everything and narrowed the list itself would be exactly the
// trust-the-wiring shape that put portrait art on a widescreen: the wrong art would still be in the
// payload, one `.filter` away from being drawn again.
//
// `chosen` comes back beside the list and is the member's CURRENT art whether or not this viewport
// offers it — viewport-only art is her own standing ruling, so a phone choice legitimately does not
// appear in the desktop list, and the room still has to paint it and still needs its version.
export type RoomArtRow = {
  slug: string; width: number; height: number; version?: number;
  orientation?: string | null; hasWide?: boolean; hasVertical?: boolean;
};
export type RoomArtAnswer<T> = { backgrounds: T[]; chosen: RoomArtRow | null };

export async function listRoomBackgrounds<T>(
  doFetch: typeof fetch = fetch,
  viewport = "desktop",
): Promise<RoomArtAnswer<T> | null> {
  try {
    const res = await doFetch(`${roomBackgroundsPath}?viewport=${encodeURIComponent(viewport)}`);
    if (!res.ok) { await res.body?.cancel?.().catch(() => {}); return null; }
    const data = (await res.json().catch(() => null)) as { backgrounds?: unknown; chosen?: unknown } | null;
    if (!Array.isArray(data?.backgrounds)) return null;
    return {
      backgrounds: data.backgrounds as T[],
      chosen: (data.chosen && typeof data.chosen === "object" ? data.chosen : null) as RoomArtRow | null,
    };
  } catch {
    return null;
  }
}

// **A SUCCESS SAYS NOTHING** — the thumbnails being there is the answer (R51/R53/R58 on the same
// question). Only the failure has anything to tell her, and until this round it had no way to.
export function roomBackgroundsNote(rows: unknown[] | null, copy: { failed: string }): string {
  return rows === null ? copy.failed : "";
}

// The stored row is jsonb and every key in it is whatever was last written, so each one is defaulted
// here ONCE rather than at each reader. `panels` is the one that matters: a non-object there used to
// be caught by `typeof`, and a reader that forgot the guard would put `undefined` where a lookup
// runs on every panel in the room.
export function roomStateFrom(state: RoomStatePatch | null): MyRoomState {
  const s = state || {};
  return {
    background: typeof s.background === "string" ? s.background : "",
    panels: s.panels && typeof s.panels === "object" ? (s.panels as Record<string, boolean>) : {},
    welcomed: Boolean(s.welcomed),
    reportsSeen: Boolean(s.reportsSeen),
    roomName: typeof s.roomName === "string" ? s.roomName : "",
  };
}

// FOUR answers, and the two in the middle are the ones a status code cannot tell apart.
//   sign-in — the session is gone (401). Nothing of hers is lost; she signs in and it saves.
//   refused — the server DELIBERATELY said no, at 200 or at 4xx/5xx, and it said why.
//   offline — the request never landed. Nothing was decided; this is transport noise (R18).
export type RoomStateAnswer =
  | { ok: true }
  | { ok: false; kind: "sign-in" }
  | { ok: false; kind: "refused"; message: string }
  | { ok: false; kind: "offline" };

// The two sentences the house owns, handed in by whoever holds the string catalog — this module
// imports nothing and cannot read it (`saved-books.ts`'s fallback-parameter shape).
export type RoomStateCopy = { signIn: string; failed: string };

// `doFetch` is a PARAMETER (R46's locked-door shape) so a gate can drive every answer the route can
// give without a browser, a session or a database.
export async function postRoomState(
  patch: RoomStatePatch,
  doFetch: typeof fetch = fetch,
): Promise<RoomStateAnswer> {
  try {
    const res = await doFetch(roomStatePath, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ state: patch }),
      cache: "no-store",
    });
    if (res.status === 401) { await res.body?.cancel?.().catch(() => {}); return { ok: false, kind: "sign-in" }; }
    const data = await res.json().catch(() => ({} as { ok?: boolean; message?: string }));
    if (!res.ok || (data as { ok?: boolean })?.ok === false) {
      const message = (data as { message?: string })?.message;
      return { ok: false, kind: "refused", message: typeof message === "string" ? message : "" };
    }
    return { ok: true };
  } catch {
    return { ok: false, kind: "offline" };
  }
}

// The sentence, derived from the answer rather than typed per surface. A save says NOTHING — this
// route runs on a tap that already changed the screen, and "Saved" under every panel she collapses
// is noise. Only a failure has something to tell her.
export function roomStateNote(answer: RoomStateAnswer, copy: RoomStateCopy): string {
  if (answer.ok) return "";
  if (answer.kind === "sign-in") return copy.signIn;
  if (answer.kind === "refused" && answer.message) return answer.message;
  return copy.failed;
}

// R92 — **A WRITE IS BUILT FROM WHAT THE ACCOUNT SAID, NEVER FROM WHAT THE SCREEN GUESSED.**
//
// This file's own header has always declared the rule: *"A PARTIAL patch, always. `room_state` is a
// jsonb MERGE at the database (LR14's law), so a surface sends the keys it owns and nothing else."*
// **`persistRoomState` in App.tsx broke it at all five call sites** — it posted the WHOLE five-key
// object on every write — which is R23's shape exactly, a file stating a law and the caller two
// files away breaking it.
//
// On its own that is survivable, because the object it sends is usually right. **What made it data
// loss is the SECOND fact: My Room's `roomState` starts as a COMPLETE, FABRICATED payload**
// (`{ background: "", panels: {}, welcomed: false, reportsSeen: false, roomName: "" }`) and the read
// that would replace it is honestly guarded — `if (active && state)`, R51's shape — so when the read
// does NOT land the fabrication stands. Then one ordinary tap (collapse a panel, dismiss a pointer,
// pick a background) posts all five fabricated keys over her real row, and she loses **her room
// name, her background, every open panel, and both one-time pointers** at once. There is no undo on
// this site.
//
// **THE DATABASE MERGE CANNOT SAVE HER AND THAT IS THE POINT.** `room_state = COALESCE(room_state,
// '{}') || EXCLUDED.room_state` protects a key that is ABSENT from the patch; it is powerless
// against a key that is PRESENT carrying a fabricated value. Every key was present.
//
// **AND IT CROSSES A ROOM BOUNDARY.** `room_state->>'roomName'` is one of the three sources in the
// COALESCE that names every member in the Living Room (nine queries in `server/db/living-room.js`),
// so blanking it does not merely rename her room — it changes the name she is drawn under on every
// post she has ever made.
//
// **WHY R81's CENSUS COULD NOT SEE IT:** that instrument matches an initial value that IS empty
// (`[]`, `null`, `{}`, `""`). This one is a five-key record with an empty collection and two empty
// strings nested ONE LEVEL DOWN inside it — a fabricated answer rather than an absent one, which is
// strictly worse, because an empty collection draws "she has none of these" while this draws five
// specific wrong facts that look exactly like a real answer. R89's lesson: widening WHERE you look
// is the easy half; widening WHAT COUNTS is the half that finds things.

// The five keys this room stores, declared ONCE so a write can be held to naming one of them.
export const ROOM_STATE_KEYS = ["background", "panels", "welcomed", "reportsSeen", "roomName"] as const;
export type RoomStateKey = (typeof ROOM_STATE_KEYS)[number];

// `landed` is the account's own answer, or null when it has not arrived — which is the ONLY thing
// that decides whether there is anything to write from. Not "did it fail" (R18's distinction is
// about what the server SAID; this is about whether it said anything at all): a read still in flight
// and a read that dropped are one fact here, because neither gives this room a row to patch.
export type RoomStateWrite =
  | { write: true; patch: Partial<MyRoomState> }
  | { write: false; reason: "not-landed" };

export function roomStateWrite<K extends RoomStateKey>(
  landed: MyRoomState | null,
  key: K,
  value: MyRoomState[K],
): RoomStateWrite {
  if (landed === null) return { write: false, reason: "not-landed" };
  // EXACTLY the key the caller named. Not a spread of what the screen currently holds — that is the
  // whole defect — and not a closed union either, because the patch type upstream is open by design.
  return { write: true, patch: { [key]: value } as Partial<MyRoomState> };
}

// **THE TAP STILL PAINTS.** A control that does nothing while a read is missing is R25's absent
// control wearing a different coat, and the paint costs her nothing: it is her own choice, applied
// to her own screen, for this session. What must not happen is that choice being written to the
// account on top of four facts nobody ever read. So the screen moves, the row is left alone, and
// this is the sentence that says so — true while the read is still in flight and true after it
// dropped, because from the write's point of view those are one fact.
export function roomStateWriteNote(write: RoomStateWrite, copy: { notLanded: string }): string {
  return write.write ? "" : copy.notLanded;
}

// **WHICH OF MY ROOM'S NINE PANELS ARE OPEN WHEN SHE ARRIVES.** This lived in App.tsx as two lines
// inside a thirty-hook component, and it is not a formatting detail: R27 measured that a COLLAPSED
// `RoomPanel` draws NO BODY, so the children of every closed block UNMOUNT rather than hide. This
// roster therefore decides what actually mounts on arrival — and it is the default side of the same
// `panels` key the write law above governs, so it belongs beside it rather than nine hooks away.
export const DEFAULT_OPEN_PANELS = ["reports", "daily", "tarot"] as const;

export function panelDefaultCollapsed(key: string): boolean {
  return !(DEFAULT_OPEN_PANELS as readonly string[]).includes(key);
}

// Her own choice wins where she has made one; `??` and not `||`, because `false` is a choice.
export function panelCollapsedIn(state: MyRoomState, key: string): boolean {
  return state.panels[key] ?? panelDefaultCollapsed(key);
}

// **THE CACHE LAW, AND IT IS THE HALF WITH TEETH FOR HER TWO PREFERENCES.** The theme and the
// language are painted first (a control that waits on a round trip reads as a control that did
// nothing) and cached on the device so the next page load has no black flash. The cache is a CACHE
// and the account overrules it — so what must never reach it is a value THE ACCOUNT LOOKED AT AND
// REFUSED. That is the one answer where remembering it makes the device and the account disagree
// permanently, on the exact value ITEM 6 / 14(c)'s REFUSE-DON'T-REPLACE law exists to keep out.
//
// R18's distinction is the whole reason this is not simply `answer.ok`: `offline` and `sign-in` are
// not decisions. The account never looked. Her choice stays on the device, where it is the only
// place it can live, and the next successful write puts it on the account.
export function keepsDeviceCache(answer: RoomStateAnswer): boolean {
  return answer.ok || answer.kind !== "refused";
}
